import { useMemo, useState } from 'react';
import * as Plot from '@observablehq/plot';
import PlotFigure from '../../PlotFigure';
import ChartOverlay from '../common/ChartOverlay';

const sectionStyle = {
    marginTop: '24px',
    display: 'grid',
    gap: '24px',
    gridTemplateColumns: 'minmax(0, 1fr)',
};

const parseTimestamp = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const numeric = typeof value === 'string' ? Number(value) : value;

    if (Number.isFinite(numeric)) {
        const millis = numeric > 1e12 ? numeric : numeric * 1000;
        const dt = new Date(millis);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    if (typeof value === 'string') {
        const dt = new Date(value);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    return null;
};

const normaliseCrashEvents = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items
        .map((item) => {
            const when =
                parseTimestamp(item?.timestamp) ||
                parseTimestamp(item?.report_time) ||
                parseTimestamp(item?.ts);

            if (!when) {
                return null;
            }

            const cause =
                item?.crash_cause ||
                item?.reset_reason ||
                item?.reset_reason_code ||
                'Crash';

            const hasCoreDump = item?.core_dump_present === true;

            return {
                when,
                cause: String(cause),
                description: item?.crash_cause || item?.reset_reason || 'Crash detected',
                coreDump: hasCoreDump,
                panicReason: item?.panic_reason,
                crashTask: item?.crash_task,
                crashPc: item?.crash_pc,
                elfSha256: item?.elf_sha256,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.when.getTime() - b.when.getTime());
};

const groupByHour = (events) => {
    const buckets = new Map();

    events.forEach((event) => {
        const bucket = new Date(event.when.getFullYear(), event.when.getMonth(), event.when.getDate(), event.when.getHours());
        const key = `${event.cause}-${bucket.toISOString()}`;
        const entry = buckets.get(key) || { cause: event.cause, time: bucket, count: 0 };
        entry.count += 1;
        buckets.set(key, entry);
    });

    return Array.from(buckets.values()).sort((a, b) => a.time.getTime() - b.time.getTime());
};

function CrashChart({ items }) {
    const events = useMemo(() => normaliseCrashEvents(items), [items]);
    const aggregated = useMemo(() => groupByHour(events), [events]);
    const [expanded, setExpanded] = useState(false);
    const [timelineExpanded, setTimelineExpanded] = useState(false);

    const causeDomain = useMemo(
        () => Array.from(new Set(events.map((event) => event.cause || 'Crash'))),
        [events]
    );

    const recent = useMemo(() => events.slice(-6).reverse(), [events]);

    const hourlyPlot = useMemo(
        () => ({
            x: { type: 'time', grid: true, label: 'Timestamp' },
            y: { grid: true, label: 'Crashes per hour' },
            color: {
                legend: true,
                type: 'ordinal',
                domain: causeDomain,
                range: [
                    '#ff6bd6',
                    '#4df7ff',
                    '#ffd93d',
                    '#8df0a1',
                    '#cfa4ff',
                    '#ff9f68',
                ].slice(0, Math.max(1, causeDomain.length)),
            },
            marks: [
                Plot.ruleY([0]),
                Plot.line(aggregated, { x: 'time', y: 'count', stroke: 'cause' }),
                Plot.dot(aggregated, {
                    x: 'time',
                    y: 'count',
                    fill: 'cause',
                    title: (d) => `${d.count} crash${d.count === 1 ? '' : 'es'}\n${d.time.toLocaleString()}\nCause: ${d.cause}`,
                }),
                Plot.tip(
                    aggregated,
                    Plot.pointerX({
                        x: 'time',
                        y: 'count',
                        stroke: 'cause',
                        title: (d) => `${d.count} crash${d.count === 1 ? '' : 'es'}\n${d.time.toLocaleString()}\nCause: ${d.cause}`,
                        pointer: 'mouse',
                        className: 'plot-tip neon-plot-tip',
                        textPadding: 12,
                        pointerSize: 14,
                        lineHeight: 1.6,
                        fontSize: 12,
                        fontWeight: 600,
                    })
                ),
            ],
        }),
        [aggregated, causeDomain]
    );

    const timelinePlot = useMemo(
        () => ({
            x: { type: 'time', grid: true },
            y: { type: 'band', tickFormat: () => '' },
            color: {
                legend: true,
                type: 'ordinal',
                domain: causeDomain,
                range: [
                    '#ff6bd6',
                    '#4df7ff',
                    '#ffd93d',
                    '#8df0a1',
                    '#cfa4ff',
                    '#ff9f68',
                ].slice(0, Math.max(1, causeDomain.length)),
            },
            marks: [
                Plot.dot(events, {
                    x: 'when',
                    y: 'cause',
                    fill: 'cause',
                    r: 6.5,
                    title: (d) => `${d.description}\n${d.when.toLocaleString()}${d.coreDump ? '\nCore dump present' : ''}`,
                }),
            ],
        }),
        [events, causeDomain]
    );

    if (!events.length) {
        return <p>No crash reports recorded for the selected interval.</p>;
    }

    return (
        <div style={sectionStyle}>
            <section className="plot-section crash-chart">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Crash volume by cause</h3>
                        <p className="plot-description">Aggregated crash counts grouped by reported cause within the selected window.</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setExpanded(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={hourlyPlot} />
            </section>

            <section className="plot-section crash-timeline">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Crash timeline</h3>
                        <p className="plot-description">Individual crash events by reported cause with optional core dump marker.</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setTimelineExpanded(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={timelinePlot} height={320} />
            </section>

            {recent.length ? (
                <aside className="alerts-summary">
                    <h4>Most recent crashes</h4>
                    <ul>
                        {recent.map((event, index) => (
                            <li key={`${event.when.getTime()}-${index}`}>
                                <div className="alerts-summary__code">{event.cause}</div>
                                <div className="alerts-summary__message">{event.description}</div>
                                <div className="alerts-summary__timestamp">{event.when.toLocaleString()}</div>
                                {event.panicReason && <div className="alerts-summary__extras">Panic: {event.panicReason}</div>}
                                {event.crashTask && <div className="alerts-summary__extras">Task: {event.crashTask}</div>}
                                {event.crashPc && <div className="alerts-summary__extras">PC: {event.crashPc}</div>}
                                {event.coreDump ? <div className="alerts-summary__extras">Core dump present</div> : null}
                            </li>
                        ))}
                    </ul>
                </aside>
            ) : null}

            {expanded ? (
                <ChartOverlay
                    title="Crash volume by cause"
                    description="Aggregated crash counts grouped by cause within the selected range."
                    onClose={() => setExpanded(false)}
                >
                    <PlotFigure options={hourlyPlot} height="min(72vh, 680px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}

            {timelineExpanded ? (
                <ChartOverlay
                    title="Crash timeline"
                    description="Individual crash reports, coloured by cause. Hover for details."
                    onClose={() => setTimelineExpanded(false)}
                >
                    <PlotFigure options={timelinePlot} height="min(72vh, 640px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}
        </div>
    );
}

export default CrashChart;
